<template>
    <ButtonComp
        class="btn-lg"
        buttonType="Primary"
        icon="fa-square-dashed-circle-plus"
        @click="openNewEventDialog"
        v-if="hasFederations && hasOrganizers"
        >{{ $tm('event.createnewevent') }}</ButtonComp
    >
    <AlertMessage
        v-if="!hasOrganizers"
        type="warning"
        >{{ $tm('quickcreate.nocreateneweventnoorganizer') }}</AlertMessage
    >
    <AlertMessage
        v-if="!hasFederations"
        type="warning"
        >{{ $tm('quickcreate.nocreateneweventnofederationforanyorganizers') }}</AlertMessage
    >

    <ModalComp
        ref="detailModal"
        size="lg"
        :min-height="50"
        title="event.createnewevent"
        title-icon="fa-square-dashed-circle-plus"
        :open-centered="false"
        @show="isModalOpen = true"
        @hide="isModalOpen = false"
        @closed="resetModel()"
    >
        <template #content>
            <ContentComp
                ref="contentComp"
                :load-state="SaveState"
                :fillContent="true"
                :showAsModal="true"
                class="p-0"
            >
                <div class="mt-3">
                    <SavedChangesMessage :visible="isSaved" />
                    <InvalidValuesMessage
                        v-if="viewcomponent != null && viewcomponent.ErrorMessages != null && viewcomponent.ErrorMessages.length > 0"
                    ></InvalidValuesMessage>
                    <CreateEventView
                        v-if="isModalOpen"
                        v-model="request"
                        ref="viewcomponent"
                    />
                </div>
            </ContentComp>
        </template>
        <template #footer>
            <ButtonComp
                button-type="Close"
                @click="detailModal?.hide()"
                >{{ $tm('general.close') }}</ButtonComp
            >

            <ButtonComp
                :loadstate="SaveState"
                button-type="Save"
                :disabled="viewcomponent == null || viewcomponent.ErrorMessages == null || viewcomponent.ErrorMessages.length > 0"
                @click="saveDetail"
                >{{ $tm('button.save') }}</ButtonComp
            >
        </template>
    </ModalComp>
</template>
<script lang="ts" setup>
import { computed, ref } from 'vue';
import { CreateEventRequest, EventinfoBase, EventLocation, ImportContent } from '@/objects/generated';
import type { IDetailModalExposed } from './Detail/IDetailModalExposed';
import { AlertMessage, ButtonComp, ContentComp, InvalidValuesMessage, ModalComp } from '@/components';
import { CreateEventView } from './views';
import { EventDao } from '@/dao';
import { useLoaders, useTaskLoader } from '@/composeables';
import SavedChangesMessage from './Alert/SavedChangesMessage.vue';
import { Router } from '@/objects/Router';
import signalrSubscribe from '@/objects/generated/signalr/signalrSubscribe';
import { randomstring } from '@/utilities/utils';
import type { ComponentExposed } from 'vue-component-type-helpers';
import { $td, $ti, $tm } from '@/locize';
import { secondsToJsDate } from '@/utilities/dateutils';
import { useCommonStoreRef, useToastStore } from '@/stores';
/**
 * We set up the request we are going to use for creating the event
 * This will be filled in with the data from the wizard.
 */
const request = ref(
    CreateEventRequest.FromJsValues({
        EventInfo: EventinfoBase.FromJsValues({
            Location: EventLocation.FromJsValues({}),
        }),
    }),
);

// all organizers we have access to
const { Organizers, Federations } = useCommonStoreRef();

const hasOrganizers = computed(() => {
    return Organizers.value.length > 0;
});

const hasFederations = computed(() => {
    return Federations.value.filter((x) => Organizers.value.map((o) => o.CountryID).includes(x.CountryID)).length > 0;
});

/**
 * The detail modal for the event
 * @type {IDetailModalExposed}
 * we use this to show the dialog
 */
const detailModal = ref<IDetailModalExposed>();

// The ref to the content component. We use this to update the loading state and the label of the loading component
const contentComp = ref<ComponentExposed<typeof ContentComp> | null>(null);

const viewcomponent = ref<InstanceType<typeof CreateEventView> | null>(null);

const { AddLoader: SaveAddLoader, LoadState: SaveState } = useTaskLoader();

const isSaved = ref<boolean>(false);

/**
 * We reset the model to the initial state
 */
const resetModel = () => {
    request.value = CreateEventRequest.FromJsValues({
        EventInfo: EventinfoBase.FromJsValues({
            EntityState: 'Added',
            Date: new Date(),
            Location: EventLocation.FromJsValues({}),
        }),
    });

    isSaved.value = false;
};

const isModalOpen = ref<boolean>(false);

const signalrGUID = randomstring('QC', '');

/**
 * Save the detail of the event
 * this is the callback function for the DetailModal and will be called when the save button is clicked
 * @param {EventinfoBase} value
 */
const saveDetail = async (value: EventinfoBase) => {
    SaveAddLoader(async () => {
        try {
            request.value.SignalrKey = signalrGUID;
            request.value.EventInfo = (await EventDao.CreateEvent(request.value)) ?? EventinfoBase.FromJsValues({});
            isSaved.value = true;

            setTimeout(() => {
                detailModal.value?.hide();
                Router.NavigateTo({ eventID: request.value.EventInfo!.ID }, 'dashboard');
            }, 1000);
        } catch (e: any) {
            let msg = '';
            if (e instanceof Error) {
                msg = e.cause?.toString() ?? e.message ?? e.toString();
            } else {
                msg = String(e);
            }
            useToastStore().addError(msg, $tm('event.errorsaving'));
        }
    }, '');
};

// We set up a signalr handler with a unique GUID
signalrSubscribe.ContestantImportRowStatus({ ImportGuid: signalrGUID }, (status) => {
    let statusText = status[0].Status ?? '';
    let totalRows = status[0].TotalRows;
    let currentRows = status[0].CurrentRow;
    let statusValues = { currentRow: currentRows, totalRows: totalRows, elapsedTime: '', timeRemaining: '', failedRows: 0 };
    // If we dont have a statusText there is nothing to show.
    if (statusText == '') return;

    // is running
    if (currentRows > 0) {
        if (totalRows == currentRows) {
            // done
            statusValues.elapsedTime = $td(secondsToJsDate(status[0].ElapsedTime! / 1000), 'TIME');
            statusValues.failedRows = status[0].FailedRows!;
            statusValues.totalRows = totalRows - status[0].FailedRows!;
        } else {
            // importing
            let diffRows = totalRows - currentRows;
            let interval = diffRows > 1000 ? 100 : diffRows > 100 ? 10 : 1;

            statusValues.currentRow = Math.floor(currentRows / interval) * interval;
            statusValues.timeRemaining = $td(secondsToJsDate((status[0].AverageTimePrRow! * (totalRows - currentRows)) / 1000), 'TIME');
        }
    }
    contentComp.value?.loadingComp?.setLabel($ti(status[0].Status!, statusValues, true));
});

/**
 * Open the new event dialog and fill it with the content of the DetailComp with the guiDef<EventinfoBase>
 */
const openNewEventDialog = () => {
    resetModel();
    detailModal.value?.show();
};

const { AddLoader } = useTaskLoader();
useLoaders(AddLoader, ['ORGANIZERS', 'FEDERATIONS']);
</script>
